import React, { useState, useEffect } from "react";
import { Grid, Stack } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker, DesktopDateTimePicker, MobileDateTimePicker } from "@mui/x-date-pickers";
import { renderTimeViewClock, renderMultiSectionDigitalClockTimeView } from "@mui/x-date-pickers/timeViewRenderers";
import dayjs from "dayjs";
import { replaceKeywordsInText } from "../../component/Charts/dashboard.utils";

export default function DateTimeRangeComponent({
  data,
  textValue,
  setTextValue,
  formaction
}) {
  const comp = data?.component;
  const sName = comp?.sName;
  const options = comp?.options;
  const dataConfig = data?.data;
  const bPayload = dataConfig?.bPayload;
  const sendFormat = dataConfig?.sSendDateFormat || dataConfig?.sSendFormat || comp?.sSendFormat;
  const sendTimeFormat = dataConfig?.sSendTimeFormat || "HH:MM:SS";

  const dateformat = (date, sourceFormat = "YYYY-MM-DD", returnFormat) => {
    if (date) {
      const parseDate = (date, format) => {
        const parts = String(date)?.split("-");
        const formatParts = format?.toLowerCase()?.split("-");
        const dateObj = {};
        formatParts?.forEach((part, index) => {
          dateObj[part] = parseInt(parts[index], 10);
        });
        return new Date(dateObj.yyyy, dateObj.mm - 1, dateObj.dd);
      };

      const formatDate = (dateObj, format) => {
        const dd = String(dateObj.getDate()).padStart(2, "0");
        const mm = String(dateObj.getMonth() + 1).padStart(2, "0");
        const yyyy = dateObj.getFullYear();
        switch (format) {
          case "dd-mm-yyyy":
            return `${dd}-${mm}-${yyyy}`;
          case "yyyy-dd-mm":
            return `${yyyy}-${dd}-${mm}`;
          case "yyyy-mm-dd":
            return `${yyyy}-${mm}-${dd}`;
          case "mm-dd-yyyy":
            return `${mm}-${dd}-${yyyy}`;
          default:
            return "Invalid date format pattern";
        }
      };

      const parsedDate = parseDate(date, sourceFormat?.toLowerCase());
      if (isNaN(parsedDate)) {
        return "Invalid date";
      }
      return formatDate(parsedDate, returnFormat?.toLowerCase());
    } else {
      return "";
    }
  };

  // Resolve date keywords like :[startofmonth]:, :[today]:, etc.
  const resolveDate = (val) => {
    if (!val) return null;
    const resolved = replaceKeywordsInText(String(val));
    return resolved === "Invalid keyword" ? val : resolved;
  };

  // Parse time string "HH:mm:ss" to { hour, minute, second }
  const parseTimeParts = (timeStr) => {
    if (!timeStr) return { hour: 0, minute: 0, second: 0 };
    const parts = timeStr.split(":");
    return {
      hour: parseInt(parts[0] || 0),
      minute: parseInt(parts[1] || 0),
      second: parseInt(parts[2] || 0)
    };
  };

  // Format dayjs to "HH:mm:ss"
  const formatTime = (d) => {
    if (!d) return "";
    return `${String(d.hour()).padStart(2, "0")}:${String(d.minute()).padStart(2, "0")}:${String(d.second()).padStart(2, "0")}`;
  };

  // Format dayjs date part to sSendFormat
  const formatDayjsDate = (d) => {
    if (!d || !d.$y) return "";
    return dateformat(
      `${d.$y}-${String(d.$M + 1).padStart(2, "0")}-${String(d.$D).padStart(2, "0")}`,
      "YYYY-MM-DD",
      sendFormat
    );
  };

  // Initial values
  const initialStartDate = resolveDate(comp?.sStartDateDefault) || resolveDate(comp?.sStartDateValue) || null;
  const initialEndDate = resolveDate(comp?.sEndDateDefault) || resolveDate(comp?.sEndDateValue) || null;
  const initialStartTime = comp?.sStartTimeDefault || comp?.sStartTimeValue || null;
  const initialEndTime = comp?.sEndTimeDefault || comp?.sEndTimeValue || null;

  // Combine date + time into single dayjs
  const buildDateTime = (dateStr, timeStr) => {
    if (!dateStr) return null;
    const t = parseTimeParts(timeStr);
    return dayjs(dateStr).hour(t.hour).minute(t.minute).second(t.second);
  };

  const [startDateTime, setStartDateTime] = useState(buildDateTime(initialStartDate, initialStartTime));
  const [endDateTime, setEndDateTime] = useState(buildDateTime(initialEndDate, initialEndTime));

  // Helper to update textValue based on bPayload
  const updateTextValue = (startDateVal, endDateVal, startTimeVal, endTimeVal) => {
    const nested = {
      sStartDateValue: startDateVal,
      sEndDateValue: endDateVal,
      sStartTimeValue: startTimeVal,
      sEndTimeValue: endTimeVal
    };
    if (bPayload) {
      const startDateField = dataConfig?.sStartValueFieldName;
      const endDateField = dataConfig?.sEndValueFieldName;
      const startTimeField = dataConfig?.sStartTimeFieldName;
      const endTimeField = dataConfig?.sEndTimeFieldName;
      setTextValue(prev => ({
        ...prev,
        ...(startDateField ? { [startDateField]: startDateVal } : {}),
        ...(endDateField ? { [endDateField]: endDateVal } : {}),
        ...(startTimeField ? { [startTimeField]: startTimeVal } : {}),
        ...(endTimeField ? { [endTimeField]: endTimeVal } : {}),
        [sName]: nested
      }));
    } else {
      setTextValue(prev => ({
        ...prev,
        [sName]: nested
      }));
    }
  };

  // Set initial textValue on mount
  useEffect(() => {
    if (sName) {
      const startDateVal = initialStartDate
        ? dateformat(initialStartDate, "YYYY-MM-DD", sendFormat)
        : "";
      const endDateVal = initialEndDate
        ? dateformat(initialEndDate, "YYYY-MM-DD", sendFormat)
        : "";
      updateTextValue(startDateVal, endDateVal, initialStartTime || "", initialEndTime || "");
    }
  }, []);

  // Handle start datetime change
  const handleStartChange = (newValue) => {
    setStartDateTime(newValue);
    if (newValue) {
      const prev = textValue?.[sName] || {};
      updateTextValue(
        formatDayjsDate(newValue),
        prev.sEndDateValue || "",
        formatTime(newValue),
        prev.sEndTimeValue || ""
      );
    }
  };

  // Handle end datetime change
  const handleEndChange = (newValue) => {
    setEndDateTime(newValue);
    if (newValue) {
      const prev = textValue?.[sName] || {};
      updateTextValue(
        prev.sStartDateValue || "",
        formatDayjsDate(newValue),
        prev.sStartTimeValue || "",
        formatTime(newValue)
      );
    }
  };

  // Build props from backend
  const formatProps = (options?.others1 && typeof options.others1 === "object") ? options.others1 : {};
  const sProps = (comp?.sProps && typeof comp.sProps === "object") ? comp.sProps : {};
  const { calendar, size, disabled, readOnly, ...restSProps } = sProps;

  // Clock type: "digital" = MultiSectionDigitalClock, "analog" = round clock, "" = default
  const clockType = comp?.sClockType?.toLowerCase();
  const clockProps = {};
  if (clockType === "digital") {
    clockProps.viewRenderers = {
      hours: renderMultiSectionDigitalClockTimeView,
      minutes: renderMultiSectionDigitalClockTimeView,
      seconds: renderMultiSectionDigitalClockTimeView
    };
  } else if (clockType === "analog") {
    clockProps.viewRenderers = {
      hours: renderTimeViewClock,
      minutes: renderTimeViewClock
    };
    clockProps.ampmInClock = true;
  }

  const commonProps = {
    disabled: disabled || false,
    readOnly: readOnly || false,
    format: formatProps?.format ? `${formatProps.format} hh:mm A` : undefined,
    views: ["year", "month", "day", "hours", "minutes"],
    closeOnSelect: false,
    ...restSProps,
    ...clockProps,
    slotProps: {
      textField: {
        helperText: comp?.sHelper,
        size: size || "small",
        id: sName
      }
    }
  };

  // Select component based on submode
  const submode = options?.submode?.trim();
  let PickerComponent = DateTimePicker;
  if (submode === "DesktopDateTimeRangePicker" || submode === "DesktopDATETIMERANGEPicker") {
    PickerComponent = DesktopDateTimePicker;
  } else if (submode === "MobileDateTimeRangePicker" || submode === "MobileDATETIMERANGEPicker") {
    PickerComponent = MobileDateTimePicker;
  }

  return (
    <Grid item {...data?.grid_props}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Stack direction="row" spacing={1} alignItems="flex-start" flexWrap="wrap">
          <PickerComponent
            label="Start"
            value={startDateTime}
            onChange={handleStartChange}
            {...commonProps}
          />
          <PickerComponent
            label="End"
            value={endDateTime}
            onChange={handleEndChange}
            {...commonProps}
          />
        </Stack>
      </LocalizationProvider>
    </Grid>
  );
}
